import { useState, useEffect } from 'react'
import { Pencil, X, Plus, Trash2, Save, ShoppingBag, Loader, Check, RefreshCw, DollarSign, FileText, Clock, CheckCircle, Truck, Banknote } from 'lucide-react'
import { formatDate } from '../../lib/calculations'
import { authedFetch } from '../../lib/authedFetch'
import { BUSINESSES as FALLBACK_BUSINESSES } from '../../data/orderMenu'

const DEPT_BUSINESS_MAP = {
  kitchen: 'Desres Kitchen',
  bakery: 'Desres Kitchen',
  tuckshop: 'Desres Kitchen',
  yawehs: "Yahweh's Delight",
}
const DEPT_BIZ_KEY_MAP = {
  kitchen: 'desres',
  bakery: 'bakery',
  tuckshop: 'desres',
  yawehs: 'yawehs',
}

const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending', icon: Clock, color: 'amber' },
  { value: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: 'blue' },
  { value: 'preparing', label: 'Preparing', icon: Clock, color: 'amber' },
  { value: 'ready', label: 'Ready', icon: Truck, color: 'green' },
  { value: 'completed', label: 'Completed', icon: Check, color: 'gray' },
]

const PAYMENT_STATUSES = [
  { value: 'paid', label: 'Paid', color: 'green', icon: CheckCircle },
  { value: 'awaiting_payment', label: 'Cash - Awaiting', color: 'amber', icon: Banknote },
  { value: 'pending', label: 'Payment Pending', color: 'blue', icon: Clock },
  { value: 'failed', label: 'Failed', color: 'red', icon: X },
]

function getPaymentBadge(paymentStatus, paymentMethod) {
  const info = PAYMENT_STATUSES.find(p => p.value === paymentStatus)
  if (!info) return null
  const methodLabel = paymentMethod === 'cash' ? 'Cash' : paymentMethod === 'card_ikhokha' ? 'iKhokha' : paymentMethod === 'card_yoco' ? 'Yoco' : 'Card'
  return { ...info, methodLabel }
}

export default function OrdersTab({ addToast, isAdmin, department, onInvoice }) {
  const deptBusiness = DEPT_BUSINESS_MAP[department]
  const deptBizKey = DEPT_BIZ_KEY_MAP[department]
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [bizFilter] = useState('all')
  const [branchFilter, setBranchFilter] = useState('all')
  const [payFilter, setPayFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('')
  const [expandedOrder, setExpandedOrder] = useState(null)
  const [showNewOrder, setShowNewOrder] = useState(false)
  const [newOrder, setNewOrder] = useState({ bizKey: deptBizKey || '', branchKey: '', name: '', whatsapp: '', email: '', collectionDate: '', paymentMethod: 'cash', items: [], note: '' })
  const [submittingOrder, setSubmittingOrder] = useState(false)
  const [editingOrder, setEditingOrder] = useState(null)
  const [savingEdit, setSavingEdit] = useState(false)

  const fetchOrders = async () => {
    try {
      const res = await authedFetch('/api/orders')
      if (!res.ok) throw new Error('Failed to fetch')
      const data = await res.json()
      setOrders(data)
    } catch (err) {
      console.error('Failed to fetch orders:', err)
      addToast('Failed to load orders', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchOrders() }, [])

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await authedFetch('/api/orders', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: orderId, status: newStatus }),
      })
      if (!res.ok) throw new Error('Failed to update')
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o))
      addToast(`Order updated to ${newStatus}${newStatus === 'ready' ? ' — WhatsApp sent!' : ''}`, 'success')
    } catch (err) {
      addToast('Failed to update order', 'error')
    }
  }

  const handleMarkPaid = async (orderId) => {
    try {
      const res = await authedFetch('/api/orders', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: orderId, payment_status: 'paid' }),
      })
      if (!res.ok) throw new Error('Failed to update')
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, payment_status: 'paid' } : o))
      addToast('Marked as paid — WhatsApp sent!', 'success')
    } catch (err) {
      addToast('Failed to update payment', 'error')
    }
  }

  const handleDelete = async (orderId) => {
    if (!confirm('Are you sure you want to delete this order? This cannot be undone.')) return
    try {
      const res = await authedFetch('/api/orders', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: orderId }),
      })
      if (!res.ok) throw new Error('Failed to delete')
      setOrders(prev => prev.filter(o => o.id !== orderId))
      addToast('Order deleted', 'success')
    } catch (err) {
      addToast('Failed to delete order', 'error')
    }
  }

  const deptOrders = deptBusiness ? orders.filter(o => o.business === deptBusiness) : orders
  const branches = [...new Set(deptOrders.map(o => o.branch).filter(Boolean))]

  let filtered = filter === 'all' ? deptOrders : deptOrders.filter(o => o.status === filter)
  if (bizFilter !== 'all') filtered = filtered.filter(o => o.business === bizFilter)
  if (branchFilter !== 'all') filtered = filtered.filter(o => o.branch === branchFilter)
  if (payFilter !== 'all') filtered = filtered.filter(o => o.payment_status === payFilter)
  if (dateFilter) filtered = filtered.filter(o => o.collection_date === dateFilter)
  const activeCount = deptOrders.filter(o => !['completed'].includes(o.status)).length

  const statusBadge = (color) => color === 'blue' ? 'bg-blue-900 text-blue-300' : color === 'amber' ? 'bg-amber-900 text-amber-300' : color === 'green' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-400'
  const payBadgeClass = (color) => color === 'green' ? 'bg-green-900 text-green-300' : color === 'amber' ? 'bg-amber-900 text-amber-300' : color === 'blue' ? 'bg-blue-900 text-blue-300' : color === 'red' ? 'bg-red-900 text-red-300' : 'bg-gray-700 text-gray-400'

  const selectedBiz = FALLBACK_BUSINESSES.find(b => b.key === newOrder.bizKey)
  const selectedBranch = selectedBiz?.branches.find(b => b.key === newOrder.branchKey)
  const newOrderTotal = newOrder.items.reduce((s, i) => s + i.price * i.quantity, 0)

  const addItemToNewOrder = (item, catLabel) => {
    setNewOrder(prev => {
      const existing = prev.items.find(i => i.id === item.id)
      if (existing) return { ...prev, items: prev.items.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i) }
      return { ...prev, items: [...prev.items, { id: item.id, description: item.name, price: item.price, quantity: 1, category: catLabel }] }
    })
  }

  const updateNewOrderQty = (itemId, delta) => {
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.map(i => i.id === itemId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter(i => i.quantity > 0),
    }))
  }

  const submitManualOrder = async () => {
    if (!newOrder.name || !newOrder.whatsapp || !newOrder.bizKey || !newOrder.branchKey || !newOrder.items.length || !newOrder.collectionDate) {
      addToast('Please fill in all required fields and add at least one item', 'error')
      return
    }
    setSubmittingOrder(true)
    try {
      const res = await authedFetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newOrder.name,
          whatsapp: newOrder.whatsapp,
          email: newOrder.email || null,
          items: newOrder.items.map(i => ({ description: i.description, price: i.price, quantity: i.quantity })),
          collection_date: newOrder.collectionDate,
          business: selectedBiz.label,
          branch: selectedBranch.label,
          total_amount: newOrderTotal,
          payment_method: newOrder.paymentMethod,
          payment_status: newOrder.paymentMethod === 'cash' ? 'awaiting_payment' : 'paid',
        }),
      })
      if (!res.ok) throw new Error('Failed to create order')
      addToast('Order created successfully', 'success')
      setShowNewOrder(false)
      setNewOrder({ bizKey: deptBizKey || '', branchKey: '', name: '', whatsapp: '', email: '', collectionDate: '', paymentMethod: 'cash', items: [], note: '' })
      fetchOrders()
    } catch {
      addToast('Failed to create order', 'error')
    } finally {
      setSubmittingOrder(false)
    }
  }

  const startEditOrder = (order) => {
    setEditingOrder({
      id: order.id,
      name: order.name || '',
      whatsapp: order.whatsapp || '',
      email: order.email || '',
      collectionDate: order.collection_date || '',
      paymentMethod: order.payment_method || 'cash',
      items: (order.items || []).map(i => ({ id: i.id || `edit-${Math.random().toString(36).slice(2)}`, description: i.description, price: i.price || 0, quantity: i.quantity || 1 })),
      note: order.note || '',
    })
  }

  const addEditItem = (item, catLabel) => {
    setEditingOrder(prev => {
      const existing = prev.items.find(i => i.description === item.name)
      if (existing) return { ...prev, items: prev.items.map(i => i.description === item.name ? { ...i, quantity: i.quantity + 1 } : i) }
      return { ...prev, items: [...prev.items, { id: `edit-${Math.random().toString(36).slice(2)}`, description: item.name, price: item.price, quantity: 1 }] }
    })
  }

  const updateEditQty = (idx, delta) => {
    setEditingOrder(prev => ({
      ...prev,
      items: prev.items.map((i, j) => j === idx ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter(i => i.quantity > 0),
    }))
  }

  const updateEditItemField = (idx, field, value) => {
    setEditingOrder(prev => ({
      ...prev,
      items: prev.items.map((i, j) => j === idx ? { ...i, [field]: field === 'description' ? value : Number(value) || 0 } : i),
    }))
  }

  const editOrderTotal = editingOrder ? editingOrder.items.reduce((s, i) => s + i.price * i.quantity, 0) : 0
  const editBiz = FALLBACK_BUSINESSES.find(b => b.key === deptBizKey)

  const saveEditOrder = async () => {
    if (!editingOrder.name || !editingOrder.whatsapp || !editingOrder.items.length) {
      addToast('Name, WhatsApp, and at least one item are required', 'error')
      return
    }
    setSavingEdit(true)
    try {
      const res = await authedFetch('/api/orders', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingOrder.id,
          name: editingOrder.name,
          whatsapp: editingOrder.whatsapp,
          email: editingOrder.email || null,
          items: editingOrder.items.map(i => ({ description: i.description, price: i.price, quantity: i.quantity })),
          collection_date: editingOrder.collectionDate,
          total_amount: editOrderTotal,
          payment_method: editingOrder.paymentMethod,
        }),
      })
      if (!res.ok) throw new Error('Failed')
      setOrders(prev => prev.map(o => o.id === editingOrder.id ? {
        ...o,
        name: editingOrder.name,
        whatsapp: editingOrder.whatsapp,
        email: editingOrder.email,
        items: editingOrder.items.map(i => ({ description: i.description, price: i.price, quantity: i.quantity })),
        collection_date: editingOrder.collectionDate,
        total_amount: editOrderTotal,
        payment_method: editingOrder.paymentMethod,
      } : o))
      addToast('Order updated', 'success')
      setEditingOrder(null)
    } catch {
      addToast('Failed to update order', 'error')
    } finally {
      setSavingEdit(false)
    }
  }

  if (loading) return <p className="text-gray-500 text-sm text-center py-8">Loading orders...</p>

  if (editingOrder) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Pencil size={16} /> Edit Order</h3>
          <button onClick={() => setEditingOrder(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Customer Name *</label>
              <input type="text" value={editingOrder.name} onChange={e => setEditingOrder(p => ({ ...p, name: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">WhatsApp *</label>
              <input type="tel" value={editingOrder.whatsapp} onChange={e => setEditingOrder(p => ({ ...p, whatsapp: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Email</label>
              <input type="email" value={editingOrder.email} onChange={e => setEditingOrder(p => ({ ...p, email: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Collection Date</label>
              <input type="date" value={editingOrder.collectionDate} onChange={e => setEditingOrder(p => ({ ...p, collectionDate: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Payment Method</label>
              <select value={editingOrder.paymentMethod} onChange={e => setEditingOrder(p => ({ ...p, paymentMethod: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                <option value="cash">Cash</option>
                <option value="card_yoco">Card (Yoco)</option>
                <option value="card_ikhokha">Card (iKhokha)</option>
              </select>
            </div>
          </div>

          {editBiz && (
            <div>
              <label className="text-xs text-gray-400 block mb-1">Add Menu Items</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto bg-gray-900 border border-gray-700 rounded p-3">
                {editBiz.categories.filter(c => !c.externalLink && c.items.length > 0).map(cat => (
                  <div key={cat.key}>
                    <p className="text-[10px] font-bold text-brand-400 uppercase tracking-wider mb-1">{cat.label}</p>
                    {cat.items.map(item => {
                      const inCart = editingOrder.items.find(i => i.description === item.name)
                      return (
                        <button
                          key={item.id}
                          onClick={() => addEditItem(item, cat.label)}
                          className={`w-full text-left flex items-center justify-between px-2 py-1 text-xs rounded mb-0.5 transition-colors ${inCart ? 'bg-brand-700/20 text-brand-300' : 'text-gray-300 hover:bg-gray-800'}`}
                        >
                          <span className="truncate">{item.name}</span>
                          <span className="text-gray-500 ml-2 shrink-0">R{item.price.toFixed(2)}{inCart ? ` (${inCart.quantity})` : ''}</span>
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {editingOrder.items.length > 0 && (
            <div className="bg-gray-900 border border-gray-700 rounded p-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Order Items</p>
              <div className="space-y-1">
                <div className="grid grid-cols-[1fr_70px_80px_80px_60px] gap-2 text-[10px] text-gray-500 uppercase tracking-wider px-1">
                  <span>Item</span><span className="text-center">Qty</span><span className="text-right">Price</span><span className="text-right">Total</span><span></span>
                </div>
                {editingOrder.items.map((item, idx) => (
                  <div key={item.id || idx} className="grid grid-cols-[1fr_70px_80px_80px_60px] gap-2 items-center">
                    <input type="text" value={item.description} onChange={e => updateEditItemField(idx, 'description', e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-white" />
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => updateEditQty(idx, -1)} className="w-5 h-5 bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-300 rounded hover:border-red-500 text-xs">−</button>
                      <span className="text-white text-xs w-4 text-center">{item.quantity}</span>
                      <button onClick={() => updateEditQty(idx, 1)} className="w-5 h-5 bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-300 rounded hover:border-green-500 text-xs">+</button>
                    </div>
                    <input type="number" min="0" step="0.01" value={item.price || ''} onChange={e => updateEditItemField(idx, 'price', e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-white text-right" />
                    <span className="text-green-400 text-xs text-right">R{(item.price * item.quantity).toFixed(2)}</span>
                    <button onClick={() => setEditingOrder(p => ({ ...p, items: p.items.filter((_, j) => j !== idx) }))} className="text-gray-500 hover:text-red-400 text-center"><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-700 mt-2 pt-2 flex justify-between">
                <span className="text-sm font-bold text-white">Total</span>
                <span className="text-sm font-bold text-green-400">R{editOrderTotal.toFixed(2)}</span>
              </div>
            </div>
          )}

          <button
            onClick={() => setEditingOrder(p => ({ ...p, items: [...p.items, { id: `custom-${Date.now()}`, description: '', price: 0, quantity: 1 }] }))}
            className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-1"
          >
            <Plus size={12} /> Add custom item
          </button>

          <div className="flex gap-3 pt-2">
            <button
              onClick={saveEditOrder}
              disabled={savingEdit}
              className="flex items-center gap-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white px-5 py-2.5 rounded text-sm font-medium"
            >
              {savingEdit ? <><Loader size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> Save Changes</>}
            </button>
            <button onClick={() => setEditingOrder(null)} className="bg-gray-700 text-gray-300 px-4 py-2.5 rounded text-sm">Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowNewOrder(!showNewOrder)}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${showNewOrder ? 'bg-gray-700 text-white' : 'bg-brand-700 hover:bg-brand-600 text-white'}`}
        >
          {showNewOrder ? <><X size={14} /> Cancel</> : <><Plus size={14} /> New Order</>}
        </button>
      </div>

      {showNewOrder && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 space-y-4">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><ShoppingBag size={16} /> Create Order on Behalf of Customer</h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Customer Name *</label>
              <input type="text" value={newOrder.name} onChange={e => setNewOrder(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">WhatsApp Number *</label>
              <input type="tel" value={newOrder.whatsapp} onChange={e => setNewOrder(p => ({ ...p, whatsapp: e.target.value }))} placeholder="e.g. 0712345678" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Email (optional)</label>
              <input type="email" value={newOrder.email} onChange={e => setNewOrder(p => ({ ...p, email: e.target.value }))} placeholder="customer@example.com" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Business *</label>
              <select value={newOrder.bizKey} onChange={e => setNewOrder(p => ({ ...p, bizKey: e.target.value, branchKey: '', items: [] }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                <option value="">Select business</option>
                {FALLBACK_BUSINESSES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Branch *</label>
              <select value={newOrder.branchKey} onChange={e => setNewOrder(p => ({ ...p, branchKey: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" disabled={!newOrder.bizKey}>
                <option value="">Select branch</option>
                {selectedBiz?.branches.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Collection Date *</label>
              <input type="date" value={newOrder.collectionDate} onChange={e => setNewOrder(p => ({ ...p, collectionDate: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Payment Method</label>
              <select value={newOrder.paymentMethod} onChange={e => setNewOrder(p => ({ ...p, paymentMethod: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                <option value="cash">Cash</option>
                <option value="card_yoco">Card (Yoco)</option>
                <option value="card_ikhokha">Card (iKhokha)</option>
              </select>
            </div>
          </div>

          {selectedBiz && (
            <div>
              <label className="text-xs text-gray-400 block mb-1">Add Menu Items *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto bg-gray-900 border border-gray-700 rounded p-3">
                {selectedBiz.categories.filter(c => !c.externalLink && c.items.length > 0).map(cat => (
                  <div key={cat.key}>
                    <p className="text-[10px] font-bold text-brand-400 uppercase tracking-wider mb-1">{cat.label}</p>
                    {cat.items.map(item => {
                      const inCart = newOrder.items.find(i => i.id === item.id)
                      return (
                        <button
                          key={item.id}
                          onClick={() => addItemToNewOrder(item, cat.label)}
                          className={`w-full text-left flex items-center justify-between px-2 py-1 text-xs rounded mb-0.5 transition-colors ${inCart ? 'bg-brand-700/20 text-brand-300' : 'text-gray-300 hover:bg-gray-800'}`}
                        >
                          <span className="truncate">{item.name}</span>
                          <span className="text-gray-500 ml-2 shrink-0">R{item.price.toFixed(2)}{inCart ? ` (${inCart.quantity})` : ''}</span>
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {newOrder.items.length > 0 && (
            <div className="bg-gray-900 border border-gray-700 rounded p-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Order Items</p>
              {newOrder.items.map(item => (
                <div key={item.id} className="flex items-center justify-between py-1 text-sm">
                  <span className="text-gray-300 flex-1 truncate">{item.description}</span>
                  <div className="flex items-center gap-2 ml-3">
                    <button onClick={() => updateNewOrderQty(item.id, -1)} className="w-6 h-6 bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-300 rounded hover:border-red-500 text-xs">−</button>
                    <span className="text-white text-xs w-4 text-center">{item.quantity}</span>
                    <button onClick={() => updateNewOrderQty(item.id, 1)} className="w-6 h-6 bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-300 rounded hover:border-green-500 text-xs">+</button>
                    <span className="text-green-400 text-xs w-16 text-right">R{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
              <div className="border-t border-gray-700 mt-2 pt-2 flex justify-between">
                <span className="text-sm font-bold text-white">Total</span>
                <span className="text-sm font-bold text-green-400">R{newOrderTotal.toFixed(2)}</span>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={submitManualOrder}
              disabled={submittingOrder || !newOrder.name || !newOrder.whatsapp || !newOrder.bizKey || !newOrder.branchKey || !newOrder.items.length || !newOrder.collectionDate}
              className="flex items-center gap-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded text-sm font-medium transition-colors"
            >
              {submittingOrder ? <><Loader size={14} className="animate-spin" /> Submitting...</> : <><Check size={14} /> Submit Order</>}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {[{ value: 'all', label: 'All' }, ...ORDER_STATUSES].map(s => (
            <button key={s.value} onClick={() => setFilter(s.value)} className={`px-3 py-1.5 rounded text-xs font-medium ${filter === s.value ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>{s.label}</button>
          ))}
        </div>
        <span className="text-xs text-gray-500 px-2">{deptBusiness}</span>
        {branches.length > 1 && (
          <div className="flex gap-1">
            {['all', ...branches].map(b => (
              <button key={b} onClick={() => setBranchFilter(b)} className={`px-3 py-1.5 rounded text-xs font-medium ${branchFilter === b ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>{b === 'all' ? 'All Branches' : b}</button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {[{ value: 'all', label: 'All Payments' }, ...PAYMENT_STATUSES].map(p => (
            <button key={p.value} onClick={() => setPayFilter(p.value)} className={`px-3 py-1.5 rounded text-xs font-medium ${payFilter === p.value ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>{p.label}</button>
          ))}
        </div>
        <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded text-xs text-gray-300 px-2 py-1.5" />
        {dateFilter && <button onClick={() => setDateFilter('')} className="text-xs text-gray-400 hover:text-white">Clear</button>}
        <span className="text-xs text-gray-500 ml-auto">{activeCount} active &middot; {filtered.length} shown</span>
        <button onClick={() => { setLoading(true); fetchOrders() }} className="text-gray-400 hover:text-white"><RefreshCw size={14} /></button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingBag size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No orders yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Business</th>
                <th className="text-left py-2 px-2">Branch</th>
                <th className="text-left py-2 px-2">Items</th>
                <th className="text-right py-2 px-2">Total</th>
                <th className="text-left py-2 px-2">Collect</th>
                <th className="text-center py-2 px-2">Status</th>
                <th className="text-center py-2 px-2">Payment</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(order => {
                const statusInfo = ORDER_STATUSES.find(s => s.value === order.status) || ORDER_STATUSES[0]
                const payBadge = getPaymentBadge(order.payment_status, order.payment_method)
                const isExpanded = expandedOrder === order.id
                return (
                  <tr key={order.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="py-2 px-2">
                      <div className="font-medium text-white">{order.name}</div>
                      <div className="text-[10px] text-gray-500">{order.whatsapp}</div>
                      {order.email && <div className="text-[10px] text-gray-500">{order.email}</div>}
                    </td>
                    <td className="py-2 px-2 text-gray-300 text-xs">{order.business}</td>
                    <td className="py-2 px-2 text-gray-300 text-xs">{order.branch}</td>
                    <td className="py-2 px-2">
                      <button onClick={() => setExpandedOrder(isExpanded ? null : order.id)} className="text-xs text-brand-400 hover:text-brand-300">
                        {(order.items || []).length} item{(order.items || []).length !== 1 ? 's' : ''}
                      </button>
                      {isExpanded && (
                        <div className="mt-1 space-y-0.5">
                          {(order.items || []).map((item, i) => (
                            <div key={i} className="text-[10px] text-gray-400">
                              {item.description} x{item.quantity}{item.price > 0 ? ` — R${(item.price * item.quantity).toFixed(2)}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right font-bold text-green-400">
                      {order.total_amount > 0 ? `R${Number(order.total_amount).toFixed(2)}` : '—'}
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-300">{formatDate(order.collection_date)}</td>
                    <td className="py-2 px-2 text-center">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${statusBadge(statusInfo.color)}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-center">
                      {payBadge && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${payBadgeClass(payBadge.color)}`}>
                          {payBadge.label}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {order.status === 'pending' && (
                          <button
                            onClick={() => handleStatusChange(order.id, 'confirmed')}
                            className="text-[10px] px-2 py-1 rounded font-medium bg-blue-900 hover:bg-blue-800 text-blue-300"
                          >
                            Confirm
                          </button>
                        )}
                        {order.status !== 'ready' && order.status !== 'completed' && (
                          <button
                            onClick={() => handleStatusChange(order.id, 'ready')}
                            className="text-[10px] px-2 py-1 rounded font-medium bg-green-900 hover:bg-green-800 text-green-300"
                          >
                            Order Ready
                          </button>
                        )}
                        {order.status === 'ready' && (
                          <button
                            onClick={() => handleStatusChange(order.id, 'completed')}
                            className="text-[10px] px-2 py-1 rounded font-medium bg-gray-700 hover:bg-gray-600 text-gray-300"
                          >
                            Complete
                          </button>
                        )}
                        {order.payment_status === 'awaiting_payment' && (
                          <button
                            onClick={() => handleMarkPaid(order.id)}
                            className="text-[10px] px-2 py-1 rounded font-medium bg-emerald-900 hover:bg-emerald-800 text-emerald-300 flex items-center gap-1"
                          >
                            <DollarSign size={10} /> Mark Paid
                          </button>
                        )}
                        <button
                          onClick={() => startEditOrder(order)}
                          className="text-[10px] px-2 py-1 rounded font-medium bg-amber-900 hover:bg-amber-800 text-amber-300 flex items-center gap-1"
                        >
                          <Pencil size={10} /> Edit
                        </button>
                        <button
                          onClick={() => onInvoice?.(order)}
                          className="text-[10px] px-2 py-1 rounded font-medium bg-purple-900 hover:bg-purple-800 text-purple-300 flex items-center gap-1"
                        >
                          <FileText size={10} /> Invoice
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="text-[10px] px-2 py-1 rounded font-medium bg-red-900 hover:bg-red-800 text-red-300 flex items-center gap-1"
                          >
                            <Trash2 size={10} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
